import React, { useCallback, useEffect, useState } from 'react';
import { erpAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import { rows, errorText, date, variantDisplay, DataTable, Loading } from '../../utils/erpHelpers';
import './ERPManagement.css';

const InventoryStockMovements: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const closeToast = useCallback(() => setMessage(null), []);
  const [locationFilter, setLocationFilter] = useState(() => sessionStorage.getItem('erp-location-filter') || '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (locationFilter) params.location = locationFilter;
      const [movementsRes, locsRes] = await Promise.all([
        erpAPI.list('stock-movements', Object.keys(params).length ? params : undefined),
        erpAPI.list('locations'),
      ]);
      setData(rows(movementsRes.data));
      setLocations(rows(locsRes.data));
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setLoading(false); }
  }, [locationFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const change = (event: Event) => {
      const location = String((event as CustomEvent).detail?.location || '');
      sessionStorage.setItem('erp-location-filter', location);
      setLocationFilter(location);
    };
    window.addEventListener('erp-location-change', change);
    return () => window.removeEventListener('erp-location-change', change);
  }, []);
  useEffect(() => {
    const fn = () => load(); window.addEventListener('admin-refresh', fn);
    return () => window.removeEventListener('admin-refresh', fn);
  }, [load]);

  const showLocationColumn = !locationFilter;

  if (loading && !data.length) return <Loading />;

  return (
    <div className="erp-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={closeToast} duration={message?.type === 'error' ? 5000 : 3500} />

      <div className="erp-workspace-toolbar">
        <div>
          <strong>Stock Movement History</strong>
          <p>Immutable audit trail of every stock change — purchases, transfers, sales, adjustments, and returns.</p>
        </div>
      </div>

      <DataTable title="Stock movement history" columns={
        ['Date', ...(showLocationColumn ? ['Location'] : []), 'Variant', 'Type', 'Physical Δ', 'Reserved Δ', 'Balance', 'Reference']
      } rows={data.map(x => [
        date(x.created_at),
        ...(showLocationColumn ? [x.location_name] : []),
        variantDisplay({ product_name: x.product_name, color: x.variant_color, size: x.variant_size, sku: x.variant_sku }),
        String(x.movement_type).replaceAll('_', ' '),
        x.quantity,
        x.reserved_quantity_change,
        x.balance_after,
        x.reference_label || (x.reference_type ? (x.movement_type === 'PURCHASE' ? x.notes : `${x.reference_type} ${x.reference_id}`) : '—')
      ])} />
    </div>
  );
};

export default InventoryStockMovements;
